import { useCallback, useEffect, useId, useRef, useState, type JSX, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

/**
 * First-run WELCOME / onboarding (Task 4, council: Creative Director + UX Director + Community Manager +
 * Behavioral Psychologist + Senior Frontend). The very first thing a new citizen sees — a warm "you've
 * ARRIVED somewhere alive and friendly" greeting, NOT a corporate product tour. It says what this world
 * IS (a Social Workspace MMORPG — companies are guilds, releases are raid bosses, the real goal is
 * belonging), what you can DO here, and teaches the REAL controls (read from the code, never invented),
 * then ends on a "come say hi" note rather than a checklist.
 *
 * Self-managing by design (no required props): on mount it reads the localStorage flag
 * `duck-duck-office-welcomed`; if absent/false it greets the player, and on ANY dismiss (the button, Esc,
 * or a backdrop click) it persists the flag so the world never greets the same citizen twice. The parent
 * just mounts `<WelcomeModal />`.
 *
 * Accessibility (ADR-0012 gate — mirrors the GuildPanel `Panel` / RpsInviteDialog focus pattern, but as a
 * TRUE blocking modal since this is a one-time full-attention greeting over a backdrop, not an in-world
 * non-blocking HUD popover):
 *   • role="dialog" + aria-modal="true", labelled by its heading + described by the intro.
 *   • Focus-managed: focus lands inside on open, a real focus-TRAP keeps Tab/Shift+Tab cycling within the
 *     card while it's open (WCAG 2.4.3), and focus is restored to the opener on close.
 *   • Esc closes, the backdrop closes, and the primary button closes — never trap the player forever.
 *   • Dual-coded throughout (icon + text, never colour/emoji-alone); honours prefers-reduced-motion for
 *     the entrance (the info is identical either way).
 *
 * Dev hook (e2e, mirrors the `__duckRps`/`__duckSocial` convention): in DEV it registers
 * `window.__duckWelcome()` → `{ open: boolean, dismiss(): void }` so the test agent can read/drive it.
 */

/** The single localStorage key gating the first-run greeting (per the task spec — keep this stable). */
const WELCOMED_KEY = 'duck-duck-office-welcomed';

/** One scannable "what you can do here" line — a warm verb-led blurb, dual-coded with a leading icon. */
interface ThingYouCanDo {
  icon: string;
  title: string;
  blurb: string;
}

/**
 * The handful of joys, in the order a new citizen meets them — kept SHORT + exciting + scannable (never a
 * wall of text). Faithful to the philosophy: reputation is peer-given trust (the most valuable currency),
 * releases are raid bosses, money buys looks not power.
 */
const THINGS_YOU_CAN_DO: ThingYouCanDo[] = [
  {
    icon: '🚶',
    title: 'Wander & meet people',
    blurb: 'Stroll the floor and bump into your guildmates — no calendar invite required.',
  },
  {
    icon: '🎙️',
    title: 'Talk by proximity',
    blurb: 'Walk up to a duck and just talk. Step away and the chatter fades — like a real room.',
  },
  {
    icon: '⭐',
    title: 'Give Reputation',
    blurb: 'Hand a teammate a little trust when they’re kind or brilliant. It’s the rarest currency here.',
  },
  {
    icon: '🎉',
    title: 'Celebrate together',
    blurb: 'Ship a release (yes, that’s the raid boss) and watch the whole office burst into confetti.',
  },
  {
    icon: '🎩',
    title: 'Make it yours',
    blurb: 'Earn coins from kind, real actions and spend them on looks. Style only — never an advantage.',
  },
];

/** One control to teach, as a clean key-cap chip group + a plain-language description (dual-coded). */
interface ControlHint {
  /** The key(s) for this action, rendered as <kbd> chips. Multiple = "or" (e.g. WASD / arrows). */
  keys: string[];
  label: string;
}

/**
 * The REAL bindings, confirmed from the code (never invented):
 *   • Move        — WASD / arrow keys           (apps/web/src/net/input.ts MOVE_KEYS + vector())
 *   • Interact    — E                           (apps/web/src/features/office/OfficePage.tsx interact effect)
 *   • Reputation  — G near a teammate           (apps/web/src/net/input.ts → 'give-reputation')
 *   • Menus       — 1 Reactions · 2 Sounds · 3 Animations · 4 Hold, then type an item's number
 *                                                (features/office/useInteractionMenus + HUD action bar)
 *   • Talk        — V to push-to-talk           (OfficePage PTT effect; only when PTT mode is on)
 * Challenging someone to Rock-Paper-Scissors is a HUD button (no key binding), so it's deliberately
 * omitted from the chips — we only ever show a control we can confirm.
 */
const CONTROLS: ControlHint[] = [
  { keys: ['W', 'A', 'S', 'D'], label: 'Walk around (or the arrow keys)' },
  { keys: ['E'], label: 'Interact with what you’re near' },
  { keys: ['G'], label: 'Give a nearby duck Reputation' },
  { keys: ['1', '2', '3', '4'], label: 'Open Reactions · Sounds · Animations · Hold (then type a number)' },
  { keys: ['V'], label: 'Hold to talk (push-to-talk)' },
];

/** A single key-cap chip — a small, tactile <kbd> in the app's glassy style. Decorative (aria-hidden). */
function KeyCap({ children }: { children: ReactNode }): JSX.Element {
  return (
    <kbd
      aria-hidden="true"
      className="inline-flex min-w-[1.75rem] items-center justify-center rounded-md border border-white/15 bg-white/10 px-2 py-1 text-xs font-bold text-white shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]"
    >
      {children}
    </kbd>
  );
}

/** Props are all OPTIONAL — the modal is self-managing. Props exist only for storybook / testing. */
export interface WelcomeModalProps {
  /** Called after the modal closes + the flag is persisted (optional; e.g. to nudge focus or analytics). */
  onClose?: () => void;
  /**
   * Force the modal open regardless of the persisted flag (storybook / testing). When set, the modal
   * shows and dismissing it does NOT write the flag (so a test can re-open it) and calls `onClose` only.
   */
  forceOpen?: boolean;
}

/**
 * The first-run welcome greeting. Mount once as `<WelcomeModal />`; it decides for itself whether to
 * appear. See the file header for the full rationale, a11y contract, and the dev hook.
 */
export function WelcomeModal({ onClose, forceOpen = false }: WelcomeModalProps = {}): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  // Decide visibility ONCE on mount from storage (lazy init so SSR/no-storage is safe). When `forceOpen`
  // is set we always start open and never touch storage on dismiss (purely a testing/storybook escape).
  const [open, setOpen] = useState<boolean>(() => {
    if (forceOpen) return true;
    try {
      return localStorage.getItem(WELCOMED_KEY) !== 'true';
    } catch {
      // Private mode / storage blocked: greet them this once rather than crash. We just can't persist.
      return true;
    }
  });

  const titleId = useId();
  const descId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  // The element to hand focus back to on close (WCAG 2.4.3). Captured on open.
  const openerRef = useRef<HTMLElement | null>(null);

  // Dismiss = persist the flag (unless forced) + close + restore focus + notify the parent. Wrapped in a
  // single guarded path so the button, Esc, and the backdrop all behave identically and persist once.
  const dismiss = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) return false; // already closed — no double-persist / double-onClose
      if (!forceOpen) {
        try {
          localStorage.setItem(WELCOMED_KEY, 'true');
        } catch {
          // Best-effort: if storage is blocked we still close gracefully (it may greet again next visit).
        }
      }
      // Restore focus to whatever opened us (best-effort), then let the parent react.
      const opener = openerRef.current;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
      onClose?.();
      return false;
    });
  }, [forceOpen, onClose]);

  // Focus-on-open (lands on the primary button so Enter/Space dismisses) + capture the opener to restore
  // to on close. Keyed on `open` so it runs cleanly each time the modal opens.
  useEffect(() => {
    if (!open) return;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // Defer to the next frame so the card (and its button) is mounted before we focus.
    const raf = requestAnimationFrame(() => {
      const card = cardRef.current;
      const primary = card?.querySelector<HTMLElement>('[data-welcome-primary]');
      (primary ?? card)?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Signal to OTHER auto-focusing dialogs that a blocking welcome is up, so they yield instead of stealing
  // focus into a dual-modal conflict (Fix 5): the RpsInviteDialog skips its focus-steal when
  // `document.body[data-welcome-open]` is set. We set it only while OPEN and remove it on close / unmount,
  // with cleanup so the attribute can never leak past dismissal. SSR/guard-safe: `document` is only touched
  // inside this effect (which never runs on the server) and existence is checked defensively.
  useEffect(() => {
    if (!open) return;
    if (typeof document === 'undefined') return;
    const { body } = document;
    if (!body) return;
    body.setAttribute('data-welcome-open', 'true');
    return () => {
      body.removeAttribute('data-welcome-open');
    };
  }, [open]);

  // Real focus-TRAP + Esc + keystroke CONTAINMENT (this is a blocking modal, unlike the non-blocking HUD
  // popovers). Because the live office engine is still running behind the backdrop, every game key is bound
  // at the WINDOW level in bubble phase — movement / emotes (1-4) / give-reputation (G) via net/input.ts,
  // and interact (E) / push-to-talk (V) via OfficePage's window keydown listeners (verified: none use
  // capture phase). Focus is pinned INSIDE this card (the primary button), and isTyping() is false for a
  // <button>, so without containment a first-run player reading the card would walk their duck, fire
  // emotes, trigger interact/PTT, and — worst — spend their rarest currency (Reputation) via G. We
  // therefore stopPropagation() on EVERY key so no keydown reaches the bubble-phase window listeners,
  // while still handling Esc (close) and Tab / Shift+Tab (focus-trap, WCAG 2.4.3) ourselves. Containment
  // is intentionally first so an early `return` can never let a key escape. (If the modal ever gains a
  // text field, the window listeners' own isTyping() guard would still ignore it — and stopping
  // propagation only blocks the GLOBAL game handlers, never the field's own typing, which happens on the
  // input element itself before the event reaches this card-level handler.)
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Contain ALL keystrokes within the modal: keep them off the window-level game-key listeners.
      e.stopPropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== 'Tab') return;
      const card = cardRef.current;
      if (!card) return;
      const focusable = Array.from(
        card.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        // Nothing tabbable but the card itself — keep focus pinned to it.
        e.preventDefault();
        card.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === card)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [dismiss],
  );

  // Containment must cover keyUP too: net/input.ts and OfficePage's PTT both bind window-level keyup
  // (movement release + V push-to-talk release). With keydown already contained those releases are mostly
  // inert, but swallowing keyup as well guarantees NO game key — down or up — ever reaches the window
  // while the modal is open, closing the hole completely. Nothing here needs Esc/Tab handling.
  const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  // Dev-only e2e hook, mirroring the `__duckRps` / `__duckSocial` shape: `{ open, dismiss() }`. Registered
  // here (self-contained) and torn down on unmount / when DEV is off. Read-only observability + a driver.
  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    (window as unknown as { __duckWelcome?: unknown }).__duckWelcome = () => ({
      open,
      dismiss,
    });
    return () => {
      delete (window as unknown as { __duckWelcome?: unknown }).__duckWelcome;
    };
  }, [open, dismiss]);

  const backdropMotion = reduceMotion
    ? {}
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
  const cardMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 16, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 16, scale: 0.98 },
        transition: { duration: 0.24, ease: 'easeOut' as const },
      };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...backdropMotion}
          // The backdrop: a soft dim over the (still-rendered) world. A click on the backdrop itself —
          // never a click that bubbled from the card — dismisses, so the player is never trapped.
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) dismiss();
          }}
          className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm sm:p-6"
        >
          <motion.div
            {...cardMotion}
            ref={cardRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            // Caps to a comfortable column on big desktops, and to the viewport height on short
            // landscape tablets (the body scrolls; the header + footer/button stay pinned + visible).
            className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/70 shadow-2xl outline-none backdrop-blur-xl sm:max-h-[88dvh]"
          >
            {/* Header — the warm "you've arrived" hello + the one-line identity of this world. */}
            <header className="border-b border-white/10 bg-gradient-to-b from-amber-400/10 to-transparent px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="text-4xl">🦆</span>
                <div className="leading-tight">
                  <h2 id={titleId} className="text-xl font-bold text-white">
                    Welcome to the office!
                  </h2>
                  <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-amber-200/80">
                    A Social Workspace MMORPG
                  </p>
                </div>
              </div>
              <p id={descId} className="mt-3 text-sm leading-relaxed text-white/80">
                You just walked into a living world where{' '}
                <span className="font-semibold text-white">companies are guilds</span> and{' '}
                <span className="font-semibold text-white">releases are raid bosses</span>. This isn’t a
                tracker and nobody’s watching the clock — it’s just a warm place to be together. Pull up a
                chair. 🪑
              </p>
            </header>

            {/* Scrollable body — the joys + the controls, kept short + scannable. */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-white/40">
                Things you can do
              </h3>
              <ul className="mt-2 flex flex-col gap-2.5">
                {THINGS_YOU_CAN_DO.map((thing) => (
                  <li key={thing.title} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-lg"
                    >
                      {thing.icon}
                    </span>
                    <span className="min-w-0 leading-snug">
                      <span className="text-sm font-semibold text-white">{thing.title}</span>
                      <span className="block text-xs text-white/65">{thing.blurb}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-white/40">
                How to get around
              </h3>
              <ul className="mt-2 flex flex-col gap-2">
                {CONTROLS.map((control) => (
                  <li
                    key={control.label}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white/5 px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-sm text-white/80">{control.label}</span>
                    <span aria-hidden="true" className="flex shrink-0 items-center gap-1">
                      {control.keys.map((k, i) => (
                        <KeyCap key={`${control.label}-${k}-${i}`}>{k}</KeyCap>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Footer — the warm "come say hi" send-off + the single primary dismiss (the e2e target). */}
            <footer className="flex flex-col gap-3 border-t border-white/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-snug text-white/55">
                Everyone here was new once. Go wave at someone — they’ll wave back. 👋
              </p>
              <button
                type="button"
                data-welcome-primary
                onClick={dismiss}
                aria-label="Start exploring"
                className="shrink-0 rounded-xl border border-amber-300/50 bg-amber-400/25 px-5 py-2.5 text-sm font-bold text-amber-50 transition hover:bg-amber-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
              >
                Start exploring
              </button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
